import { Add, Check, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, deDE } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { FullScreenDialog } from '../core/FullScreenDialog';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { ShowWasSaved } from '../core/ShowWasSaved';
import {
  useGetArnoTopObjects,
  useGetLegalRequirementTemplates,
} from '../hooks/data';
import { LegalRequirementTemplateEdit } from './LegalRequirementTemplateEdit';
import { LegalTransactionTypeEdit } from './LegalTransactionTypeEdit';
import { ILegalTransactionType } from '../Interfaces/ILegalTransactionType';
import { useFetch } from '../hooks/useFetch';

// Colum Rechtsgeschäftsart
const columnsLegalTransactionType: GridColDef[] = [
  { field: 'idLegalTransactionType', headerName: 'Nr.', flex: 1 },
  {
    field: 'isTemplate',
    headerName: 'Vorlage',
    flex: 2,
    renderCell: params =>
      Boolean(params.row.isTemplate) ? <Check /> : <Close />,
  },
  {
    field: 'withDialog',
    headerName: 'Dialog',
    flex: 2,
    renderCell: params =>
      Boolean(params.row.withDialog) ? <Check /> : <Close />,
  },
  { field: 'LegalTransactionType', headerName: 'Art', flex: 3 },
];
// Colum Maßnahmen-Katalog
export const columnsLegalRequirementTemplate: GridColDef[] = [
  { field: 'idLegalRequirementTemplate', headerName: 'Nr.', flex: 1 },
  {
    field: 'isInterneLegalRequirement',
    headerName: 'Interne Maßnahme',
    flex: 2,
    renderCell: params =>
      Boolean(params.row.isInterneLegalRequirement) ? <Check /> : <Close />,
  },
  {
    field: 'canUploadFilesEmployees',
    headerName: 'Datenupload Mitarbeiter',
    flex: 2,
    renderCell: params =>
      Boolean(params.row.canUploadFilesEmployees) ? <Check /> : <Close />,
  },
  {
    field: 'canUploadFilesClient',
    headerName: 'Datenupload Mandanten',
    flex: 2,
    renderCell: params =>
      Boolean(params.row.canUploadFilesClient) ? <Check /> : <Close />,
  },
  {
    field: 'sendMail',
    headerName: 'Auto. E-Mail',
    flex: 2,
    renderCell: params =>
      Boolean(params.row.sendMail) ? <Check /> : <Close />,
  },
  { field: 'LegalRequirementTemplate', headerName: 'Maßnahme', flex: 3 },
];

export const LegalTransactionTypeOverview: React.FC = () => {
  //
  // Maßnahmen-Katalog
  const {
    legalRequirementTemplates,
    isLoadingLegalRequirementTemplates,
    refetchLegalRequirementTemplates,
    isRefetchingLegalRequirementTemplates,
  } = useGetLegalRequirementTemplates();
  // useStates
  const [currentRequirementTemplateId, setCurrentRequirementTemplateId] =
    useState<number | null>(null);
  const [wasSaved, setWasSaved] = useState(false);
  const [
    legalRequirementTemplateWasSuccessfullySaved,
    setLegalRequirementTemplateWasSuccessfullySaved,
  ] = useState(true);
  const [
    searchTermLegalRequirementTemplate,
    setSearchTermLegalRequirementTemplate,
  ] = useState('');
  useEffect(() => {
    if (legalRequirementTemplateWasSuccessfullySaved) {
      refetchLegalRequirementTemplates();
      setLegalRequirementTemplateWasSuccessfullySaved(false);
    }
  }, [legalRequirementTemplateWasSuccessfullySaved]);

  //
  // Rechtsgeschäftsarten
  const [
    transactionTypeArray,
    setTransactionTypeArray,
    wasSuccessfullyTransactionTypeArray,
  ] = useFetch<ILegalTransactionType[]>('/legaltransactiontype');
  const { arnoTopObjects } = useGetArnoTopObjects();
  const [idLegalTransactionType, setIdLegalTransactionType] = useState<
    number | null
  >(null);
  const [legalTransactionTypeName, setLegalTransactionTypeName] =
    useState<string>('');
  const [wasSuccessfullySaved, setWasSuccessfullySaved] = useState(true);
  const [searchTermTransactionType, setSearchTermTransactionType] =
    useState('');
  //
  //
  const [isOpenAreYourSure, setIsOpenAreYourSure] = useState(false);

  const handleClose = () => {
    setIsOpenAreYourSure(true);
  };

  const handleFinalOpen = () => {
    setIsOpenAreYourSure(false);
    setIdLegalTransactionType(null);
    setLegalTransactionTypeName('');
  };

  const handleAfterRemove = () => {
    if (transactionTypeArray !== undefined && idLegalTransactionType !== null) {
      setTransactionTypeArray([
        ...transactionTypeArray.filter(
          x => x.idLegalTransactionType !== idLegalTransactionType
        ),
      ]);
      setIdLegalTransactionType(null);
      setLegalTransactionTypeName('');
    }
  };

  const handleSearchTermTransactionType = () => {
    if (!transactionTypeArray || transactionTypeArray.length === 0) {
      return [];
    } else if (searchTermTransactionType === '') {
      return transactionTypeArray;
    } else {
      return transactionTypeArray.filter(
        x =>
          String(x.LegalTransactionType || '')
            .toLocaleLowerCase()
            .includes(searchTermTransactionType.toLocaleLowerCase()) ||
          String(x.idLegalTransactionType || '')
            .toLocaleLowerCase()
            .includes(searchTermTransactionType.toLocaleLowerCase())
      );
    }
  };

  const handleSearchTermRequirements = () => {
    if (!legalRequirementTemplates || legalRequirementTemplates.length === 0) {
      return [];
    } else if (searchTermLegalRequirementTemplate === '') {
      return legalRequirementTemplates.filter(x => x.isTemplate);
    } else {
      return legalRequirementTemplates.filter(
        x =>
          x.isTemplate &&
          (String(x.LegalRequirementTemplate || '')
            .toLocaleLowerCase()
            .includes(searchTermLegalRequirementTemplate.toLocaleLowerCase()) ||
            String(x.idLegalRequirementTemplate || '')
              .toLocaleLowerCase()
              .includes(searchTermLegalRequirementTemplate.toLocaleLowerCase()))
      );
    }
  };

  if (!wasSuccessfullyTransactionTypeArray) {
    return <>Fehler!</>;
  } else if (
    transactionTypeArray === undefined ||
    legalRequirementTemplates === undefined
  ) {
    return <LoadingSpinner height='50vh' />;
  } else {
    return (
      <Box sx={{ m: 2, mt: 0 }}>
        <ShowWasSaved
          wasSaved={wasSaved}
          setWasSaved={setWasSaved}
          wasSuccessfullySaved={wasSuccessfullySaved}
        />

        <Dialog open={isOpenAreYourSure} onClose={() => {}}>
          <DialogTitle>Warnung</DialogTitle>
          <DialogContent>
            Sind Sie sicher, dass Sie die Rechtsgeschäftart ohne speichern
            schließen wollen?
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={handleFinalOpen}>
              Schließen
            </Button>
            <Button
              variant='contained'
              onClick={() => setIsOpenAreYourSure(false)}
              color='success'
            >
              Abbruch
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={currentRequirementTemplateId !== null}
          onClose={() => setCurrentRequirementTemplateId(null)}
          maxWidth='lg'
          fullWidth
        >
          {currentRequirementTemplateId !== null && (
            <LegalRequirementTemplateEdit
              currentRequirementTemplateid={currentRequirementTemplateId}
              setCurrentRequirementTemplateid={setCurrentRequirementTemplateId}
              setWasSaved={setWasSaved}
              title={
                currentRequirementTemplateId === -1
                  ? 'Maßnahme hinzufügen'
                  : 'Maßnahme bearbeiten'
              }
              setLegalRequirementTemplateWasSuccessfullySaved={
                setLegalRequirementTemplateWasSuccessfullySaved
              }
            />
          )}
        </Dialog>

        <FullScreenDialog
          isOpen={
            idLegalTransactionType !== null && legalTransactionTypeName !== ''
          }
          onClose={handleClose}
          title={`Rechtsgeschäftsart - ${legalTransactionTypeName}`}
          customCloseBtnFunc={() => {
            setIdLegalTransactionType(null);
            setLegalTransactionTypeName('');
          }}
          disablePadding={true}
        >
          <LegalTransactionTypeEdit
            key={`idLegalTransactionType-${idLegalTransactionType}`}
            idLegalTransactionType={
              idLegalTransactionType === null ? -1 : idLegalTransactionType
            }
            transactionTypeArray={transactionTypeArray}
            setTransactionTypeArray={setTransactionTypeArray}
            setWasSaved={setWasSaved}
            setWasSavedSuccessfully={setWasSuccessfullySaved}
            handleAfterRemove={handleAfterRemove}
            arnoTopObjects={arnoTopObjects}
          />
        </FullScreenDialog>

        <Typography variant='h4'>Rechtsgeschäftsarten</Typography>

        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant='h5'>
              Maßnahmen Katalog
              <IconButton
                sx={{ float: 'right' }}
                onClick={() => {
                  setCurrentRequirementTemplateId(-1);
                }}
              >
                <Add />
              </IconButton>
            </Typography>
            <br />

            <TextField
              sx={{ mt: 2, mb: 2 }}
              label='Suche'
              fullWidth
              value={searchTermLegalRequirementTemplate}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTermLegalRequirementTemplate(event.target.value)
              }
            />

            {isLoadingLegalRequirementTemplates ||
            isRefetchingLegalRequirementTemplates ? (
              <LoadingSpinner height='50vh' />
            ) : (
              <DataGrid
                localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
                rows={handleSearchTermRequirements()}
                columns={columnsLegalRequirementTemplate}
                getRowId={row => row.idLegalRequirementTemplate}
                onRowClick={params => {
                  setCurrentRequirementTemplateId(Number(params.id));
                }}
              />
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant='h5'>
              Rechtsgeschäftsarten
              <IconButton
                sx={{ float: 'right' }}
                onClick={() => {
                  setIdLegalTransactionType(-1);
                  setLegalTransactionTypeName('Neue Rechtsgeschäftsart');
                }}
              >
                <Add />
              </IconButton>
            </Typography>
            <br />

            <TextField
              sx={{ mt: 2, mb: 2 }}
              label='Suche'
              fullWidth
              value={searchTermTransactionType}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTermTransactionType(event.target.value)
              }
            />

            <DataGrid
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
              rows={handleSearchTermTransactionType()}
              columns={columnsLegalTransactionType}
              getRowId={row => row.idLegalTransactionType}
              onRowClick={params => {
                setLegalTransactionTypeName(params.row.LegalTransactionType);
                setIdLegalTransactionType(
                  Number(params.row.idLegalTransactionType)
                );
              }}
            />
          </Grid>
        </Grid>
      </Box>
    );
  }
};
